import { Accordion } from '../../../../../common/Accordion';
import { Icon } from '../../../../../common/Icon';
import { TextLink } from '../../../../../components/TextLink';
import { GroupedTicketData } from '../../../hooks/useSprintTimeline';

import { CheckInCard } from './CheckInCard';
import { TimelineItem } from './TimelineItem';

export function TicketAccordion({
  ticket,
}: {
  ticket: GroupedTicketData;
}): JSX.Element {
  const hasCheckIns = Object.keys(ticket.checkIns || {}).length > 0;

  return (
    <Accordion
      key={ticket.id}
      title={
        <div className="flex justify-between w-full items-center gap-4">
          <div className="max-w-[50%] min-w-0">
            <TextLink
              href={ticket.externalLink}
              className="w-fit"
              target="_blank"
            >
              <div className="flex flex-row items-center gap-2">
                <Icon
                  name="checkbox"
                  className="text-[#1677FF] flex-shrink-0"
                />
                <div className="flex items-center gap-1 min-w-0">
                  <span className="truncate">
                    {`${ticket.ticket_number}: ${ticket.title}`}
                  </span>
                  <Icon name="link" className="w-4 h-4 flex-shrink-0" />
                </div>
              </div>
            </TextLink>
          </div>
          <div className="flex items-center justify-center w-1/4 ml-2 relative">
            <div className="flex items-center gap-2 w-full">
              <span className="text-gray-800 font-medium w-10 text-right">
                {ticket.currentCompletionPercentage}%
              </span>
              <div className="flex-1 h-2 bg-gray-200 rounded">
                <div
                  className="h-2 bg-blue-500 rounded"
                  style={{
                    width: `${ticket.currentCompletionPercentage}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      }
    >
      {!hasCheckIns ? (
        <div className="text-gray-500 text-sm py-4 text-center">
          No check-ins recorded for this ticket in this period
        </div>
      ) : (
        Object.keys(ticket.checkIns)
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
          .map((dateKey, idx, arr) => {
            const checkInsForDate = ticket.checkIns[dateKey];
            const firstCheckIn = checkInsForDate[0];
            const lastCheckIn = checkInsForDate[checkInsForDate.length - 1];
            const maxCompletion = Math.max(
              ...checkInsForDate.map(
                (ci) => ci.currentCompletionPercentage ?? 0,
              ),
            );
            const delta =
              (lastCheckIn.currentCompletionPercentage ?? 0) -
              (firstCheckIn.previousCompletionPercentage ?? 0);

            return (
              <TimelineItem
                key={dateKey}
                date={dateKey}
                maxCompletion={maxCompletion}
                delta={delta}
                showLine={idx < arr.length - 1}
              >
                {checkInsForDate.map((checkIn) => (
                  <CheckInCard
                    key={`${checkIn.created_at}-${checkIn.currentCompletionPercentage}`}
                    description={checkIn.description}
                    blockers={checkIn.blockers}
                    wipLinks={checkIn.wipLinks}
                  />
                ))}
              </TimelineItem>
            );
          })
      )}
    </Accordion>
  );
}
